// Main pairing screen component

import React, { useEffect } from 'react';
import { usePairingFlow } from '../../hooks/usePairingFlow';
import PairingCodeInput from './PairingCodeInput';
import ConnectionStatus from './ConnectionStatus';
import VirtualKeyboard from '../VirtualKeyboard/VirtualKeyboard';
import styles from './PairingScreen.module.css';

const PairingScreen = ({ onPairingComplete, wsState }) => {
  const {
    pairingState,
    pairingCode,
    error,
    daysUntilExpiry,
    updatePairingCode,
    submitPairingCode,
    retryPairing,
    clearAndRestart
  } = usePairingFlow();

  const {
    isConnected,
    isAuthenticated,
    isMainAppConnected,
    error: wsError
  } = wsState;

  // Navigate to main app when fully paired
  useEffect(() => {
    if (isAuthenticated && isMainAppConnected && onPairingComplete) {
      setTimeout(() => {
        onPairingComplete();
      }, 2000);
    }
  }, [isAuthenticated, isMainAppConnected, onPairingComplete]);

  const renderContent = () => {
    switch (pairingState) {
      case 'CHECKING_TOKEN':
        return (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <p className={styles.loadingText}>Checking for existing pairing...</p>
          </div>
        );

      case 'NO_TOKEN':
      case 'ENTERING_CODE':
        return (
          <div className={styles.pairingContainer}>
            <h1 className={styles.title}>Pair Your VR Device</h1>
            <p className={styles.subtitle}>
              Enter the 6-digit code displayed on your main application
            </p>

            <PairingCodeInput
              value={pairingCode}
              onChange={updatePairingCode}
              onSubmit={submitPairingCode}
              disabled={false}
            />

            {error && (
              <div className={styles.error}>
                {error}
              </div>
            )}

            <button
              onClick={submitPairingCode}
              disabled={pairingCode.length !== 6}
              className={styles.submitButton}
            >
              Pair Device
            </button>

            <VirtualKeyboard
              onKeyPress={(key) => {
                if (key === 'backspace') {
                  updatePairingCode(pairingCode.slice(0, -1));
                } else if (key >= '0' && key <= '9') {
                  updatePairingCode(pairingCode + key);
                }
              }}
            />
          </div>
        );

      case 'VALIDATING_CODE':
        return (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <p className={styles.loadingText}>Validating pairing code...</p>
          </div>
        );

      case 'PAIRING_ERROR':
        return (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>✗</div>
            <h2 className={styles.errorTitle}>Pairing Failed</h2>
            <p className={styles.errorMessage}>{error}</p>
            <button onClick={retryPairing} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        );

      case 'PAIRING_SUCCESS':
        return (
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle}>Pairing Successful!</h2>
            <p className={styles.successMessage}>Connecting to server...</p>
          </div>
        );

      case 'TOKEN_READY':
        if (!isConnected) {
          return (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Connecting to server...</p>
            </div>
          );
        }

        if (!isAuthenticated) {
          return (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Authenticating...</p>
            </div>
          );
        }

        return (
          <div className={styles.statusContainer}>
            <h1 className={styles.title}>VR Device Paired</h1>

            <ConnectionStatus
              isWebSocketConnected={isConnected}
              isAuthenticated={isAuthenticated}
              isMainAppConnected={isMainAppConnected}
            />

            {isMainAppConnected && (
              <div className={styles.readyMessage}>
                <div className={styles.readyIcon}>✓</div>
                <h2>Ready to Start!</h2>
                <p>Your VR device is connected and ready to use</p>
              </div>
            )}

            {wsError && (
              <div className={styles.error}>{wsError}</div>
            )}

            <div className={styles.tokenInfo}>
              <p>Token expires in {daysUntilExpiry} days</p>
              <button onClick={clearAndRestart} className={styles.clearButton}>
                Clear Token & Re-pair
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.screen}>
      {renderContent()}
    </div>
  );
};

export default PairingScreen;
